import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Search, RefreshCw, Package, Filter, Eye, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import SmartPagination from '@/components/SmartPagination';
import ProductDetailModal from './ProductDetailModal';
import { useCurrency } from '@/lib/currency-context';
import { admin } from '@/lib/api';
import { buildProductPath } from '@/lib/product-url';
import { toast } from 'sonner';

const ProductsTab = ({
  setDeleteDialog,
  hasPermission,
  initialProductId = null,
  initialModalMode = 'view',
  onProductModalClose,
}) => {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const pageSize = 50;

  const fetchProducts = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const params = { page, limit: pageSize };
      if (search) params.search = search;
      const res = await admin.products(params);
      const data = res.data;
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch {
      toast.error('Urunler yuklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    if (initialProductId) {
      setSelectedProduct(initialProductId);
      setModalMode(initialModalMode || 'view');
    }
  }, [initialProductId, initialModalMode]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const timer = setTimeout(() => {
      setSearch((prev) => (prev === trimmed ? prev : trimmed));
    }, 700);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  return (
    <Card data-testid="admin-products-tab">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5" />
            {t('admin.totalProducts')} ({total.toLocaleString('tr-TR')})
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <form onSubmit={handleSearch} className="flex items-center gap-1.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  placeholder="Urun ara... (isim, SKU, marka)"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pl-8 h-8 w-64 text-sm"
                  data-testid="products-search-input"
                />
              </div>
              <Button type="submit" size="sm" variant="outline" className="h-8" data-testid="products-search-btn">
                <Search className="w-3.5 h-3.5" />
              </Button>
            </form>
            {search && (
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={clearSearch}>
                <Filter className="w-3 h-3 mr-1" /> Temizle
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => fetchProducts(true)} className="h-8" data-testid="products-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Yenile
            </Button>
          </div>
        </div>
        {search && (
          <p className="text-xs text-muted-foreground mt-1">
            "{search}" icin {total.toLocaleString('tr-TR')} sonuc bulundu
          </p>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-800" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            {search ? 'Aramayla eslesen urun bulunamadi' : 'Henuz urun eklenmemis'}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>{t('seller.productName')}</TableHead>
                  <TableHead>{t('seller.price')}</TableHead>
                  <TableHead>{t('seller.stock')}</TableHead>
                  <TableHead>{t('seller.category')}</TableHead>
                  <TableHead>Tedarikci</TableHead>
                  <TableHead>Marka</TableHead>
                  <TableHead className="text-right w-32">Islemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product, idx) => (
                  <TableRow key={product.id} data-testid={`product-row-${idx}`}>
                    <TableCell className="text-xs text-muted-foreground">
                      {(page - 1) * pageSize + idx + 1}
                    </TableCell>
                    <TableCell className="font-medium max-w-xs truncate">
                      <div className="flex items-center gap-2">
                        {product.images?.[0] && (
                          <a href={buildProductPath('tr', product)} target="_blank" rel="noopener noreferrer" className="shrink-0" data-testid={`product-image-link-${idx}`}>
                            <img src={product.images[0]?.startsWith('/uploads/') ? product.images[0].replace('/uploads/', '/api/uploads/') : product.images[0]} alt="" className="w-8 h-8 rounded object-cover border hover:ring-2 hover:ring-blue-400 cursor-pointer transition-all" />
                          </a>
                        )}
                        <span className="truncate">{product.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatPrice(product.price, product.currency || 'EUR')}</TableCell>
                    <TableCell>
                      <Badge variant={product.stock > 0 ? "default" : "destructive"}>
                        {product.stock} {t('seller.inStock')}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize text-sm">{product.category}</TableCell>
                    <TableCell className="text-sm text-gray-700 max-w-[120px] truncate" title={product.seller_name || ''}>
                      {product.seller_name || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{product.brand || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => { setSelectedProduct(product.id); setModalMode('view'); }}
                          data-testid={`view-product-${idx}`}
                          title="Goruntule"
                        >
                          <Eye className="w-3.5 h-3.5 text-blue-600" />
                        </Button>
                        {hasPermission('products.edit') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => { setSelectedProduct(product.id); setModalMode('edit'); }}
                            data-testid={`edit-product-${idx}`}
                            title="Duzenle"
                          >
                            <Pencil className="w-3.5 h-3.5 text-amber-600" />
                          </Button>
                        )}
                        {hasPermission('products.delete') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setDeleteDialog({ open: true, type: 'product', id: product.id, name: product.name })}
                            data-testid={`delete-product-${idx}`}
                            title="Sil"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <div className="pt-3 border-t mt-4">
                <SmartPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} totalItems={total} pageSize={pageSize} />
              </div>
            )}
          </>
        )}
      </CardContent>

      <ProductDetailModal
        productId={selectedProduct}
        open={!!selectedProduct}
        onClose={() => {
          setSelectedProduct(null);
          onProductModalClose?.();
        }}
        mode={modalMode}
        hasPermission={hasPermission}
        onUpdated={() => fetchProducts(true)}
      />
    </Card>
  );
};

export default ProductsTab;
